import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import gsap from "gsap";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import { useHistory } from "react-router-dom";
import { request } from "hooks/useOkoApi/useOkoApi";
import { colors, constants, logEducatorDashboard, LogLevel } from "utils";
import { trackClickedButton } from "utils/analytics";
import { SelectDropdown } from "components/shared/FilterDropdowns/FilterDropdowns";
import { classDisplayName } from "components/shared/ClassroomFilter/groupClassesBySchoolYear";
import { EditClassButton } from "./EditClassButton";
import { AssignmentAccordion } from "./AssignmentAccordion";
import { UnassignedChip } from "./UnassignedCodesRail";
import { ArchiveLessonModal } from "./ArchiveLessonModal";
import { ClearGroupCodesModal } from "./ClearGroupCodesModal";
import { ViewQuestionsModal } from "./ViewQuestionsModal";
import { resolveGroupCardTitle } from "utils/groupCardTitle";
import { ReuseFilterModal, reuseFilterCandidates } from "./ReuseFilterModal";
import { GroupsEmptyState } from "./GroupsEmptyState";
import { WarmUpEmptyStates } from "./WarmUpEmptyStates";
import { GroupsSkeleton } from "./GroupsSkeleton";
import { CreateGroupsBar } from "./CreateGroupsBar";
import { createControlsFor, warmUpPickerUrl } from "./warmUpEntry";
import { QuickGroupsSection } from "./QuickGroupsSection";
import { WarmUpGroupsLoadError } from "./WarmUpGroupsLoadError";
import { ListingHeader } from "./ListingTitle";
import { FocusedEditHeader } from "./FocusedEditHeader";
import { sortBundles, STANDARD_SORT_OPTIONS } from "./assignmentDraft";
import { normalizeGroupAssignments, patchGroupAssignments, applyQuestionSet } from "hooks/useCurriculum/useCurriculum";
import { useSessionLength, DEFAULT_SESSION_LENGTH } from "./useSessionLength";
import { CopyLessonModal } from "./CopyLessonModal";
import { useLastFocus } from "hooks/useLastFocus/useLastFocus";
import { useFeatureFlagStatus } from "hooks/useFeatureFlag";
import { manageGroupsLayoutFor } from "./manageGroupsLayout";
import { OneListPage } from "./OneListPage";
import { DEFAULT_LIST_SETTINGS, oneListCardProps } from "./oneListCards";
import "./dashboard.css";

const compareCode = (a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

// Groups here are built around one core standard rather than a daily lesson, so
// the shared Create Groups copy gets a standards-flow tail.
const LESSON_GROUPS_DETAIL =
  "align multiple groups with the core standard, its prerequisites, and extension standards.";

// Stable signature of an assignment state used to detect unsaved changes.
const signature = (bundles, unassigned) => JSON.stringify({
  u: [...unassigned].sort(compareCode),
  b: bundles
    .map((b) => [
      b.assignment_id,
      [...b.assignments]
        .map((a) => `${a.group_code}|${a.standard_code}|${a.track_type}`)
        .sort(),
    ])
    .sort((x, y) => x[0].localeCompare(y[0])),
});

// The unified read nests the bundle's standard; the accordion + cards want the
// flat primary_* pair the legacy payload had, so flatten it here. Rows are spread
// verbatim, which is what carries question_set_id / question_count through — the
// fields the legacy payload never had.
const toDraft = (data) => ({
  unassigned: [...(data?.unassigned_codes || [])].sort(compareCode),
  // Standard bundles only. The legacy read this replaced filtered lesson bundles
  // out server-side (`target_standard_code is not None`); the unified read returns
  // both kinds, and this view edits through the standard-only `groups/` endpoints,
  // which reject a lesson bundle with a 400. Lesson bundles belong to
  // LessonGroupsList.
  bundles: (data?.lessons || [])
    .filter((b) => (b.bundle_type || (b.standard ? "standard" : "lesson")) === "standard")
    .map((b) => ({
    assignment_id: b.assignment_id,
    bundle_type: b.bundle_type,
    standard: b.standard || null,
    primary_standard_code: b.standard?.standard_code || b.primary_standard_code || "",
    primary_standard_name: b.standard?.standard_name || b.primary_standard_name || "",
    last_updated_at: b.last_updated_at,
    assignments: (b.assignments || [])
      .filter((a) => a.track_type && a.track_type !== "no_assignment")
      .map((a) => ({ ...a })),
    })),
});

export const ClassroomAssignmentView = ({
  classroomId,
  classes,
  selectedClassId,
  onClassChange,
  onEditClass,
  quickGroups = [],
  onQuickGroupsChanged,
  warmUpAssignments = [],
  warmUpLoading = false,
  warmUpError = null,
  refetchWarmUps,
  replaceWarmUpAssignment,
  // Copy to other classes: the account's classes carry the per-class
  // assignment_summary the modal filters on; onLessonCopied raises the response's
  // copies for the toasts. The action only renders when onLessonCopied is wired.
  onLessonCopied,
  onAccountRefresh,
  // Every code of the class (quick and warm-up codes included), for the
  // one list's "{a} of {t} Group Codes Available".
  totalCodes,
  // The quick-group and warm-up reads have answered once for the class.
  sourcesSettled = true,
  listSettings = DEFAULT_LIST_SETTINGS,
  onListSettingsChange,
  sourcesReadAt,
}) => {
  const hasQuickGroups = quickGroups.length > 0;
  const hasWarmUps = warmUpAssignments.length > 0;
  const history = useHistory();
  const warmUpChallenges = useFeatureFlagStatus("warmUpChallenges");
  const layout = manageGroupsLayoutFor(warmUpChallenges);
  const [serverData, setServerData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeDragCode, setActiveDragCode] = useState(null);
  const [viewQuestionsTarget, setViewQuestionsTarget] = useState(null);
  const [reuseOffer, setReuseOffer] = useState(null);
  const [savingQuestionFilter, setSavingQuestionFilter] = useState(false);
  const [questionFilterSaveError, setQuestionFilterSaveError] = useState(null);
  const [copyTarget, setCopyTarget] = useState(null);
  // Focused edit mode (Figma 4884-52207): editing drops the page to the edited
  // bundle alone. isEditing is view-wide here, so track which bundle opened it.
  const [focusedId, setFocusedId] = useState(null);
  const stackRef = useRef(null);
  const didMountRef = useRef(false);

  useLayoutEffect(() => {
    const el = stackRef.current;
    if (!didMountRef.current) { didMountRef.current = true; return undefined; }
    if (!el) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) return undefined;
    const ctx = gsap.context(() => {
      gsap.from(el, { autoAlpha: 0, y: 12, duration: 0.35, ease: "power2.out" });
    }, stackRef);
    return () => ctx.revert();
  }, [focusedId]);

  const enterEdit = (bundle) => {
    setFocusedId(bundle?.assignment_id ?? null);
    setIsEditing(true);
  };
  const leaveEdit = () => setFocusedId(null);
  const [isEditing, setIsEditing] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [isClearModalOpen, setIsClearModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState("recent");
  // Set when the class's last warm-up card is archived: focus then goes to the
  // Create Groups button, or to the empty state's title if nothing is left.
  const [focusAfterLastWarmUp, setFocusAfterLastWarmUp] = useState(false);
  const clearFocusAfterLastWarmUp = useCallback(() => setFocusAfterLastWarmUp(false), []);
  useEffect(() => {
    setFocusAfterLastWarmUp(false);
  }, [classroomId]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor)
  );

  const fetchAssignment = useCallback(async () => {
    if (!classroomId) return;
    setIsLoading(true);
    setError(null);
    try {
      const fresh = normalizeGroupAssignments(
        await request(`curriculum/classrooms/${classroomId}/group-assignments`, { method: "GET" }),
      );
      setServerData(fresh);
      setDraft(toDraft(fresh));
    } catch (e) {
      logEducatorDashboard("ClassroomAssignmentView: failed to load assignment", {
        level: LogLevel.WARNING,
        event: "load_error",
        statusCode: e?.status,
        endpoint: `curriculum/classrooms/${classroomId}/group-assignments`,
      });
      setError(e?.detail || e?.message || "Failed to load group assignments.");
    } finally {
      setIsLoading(false);
    }
  }, [classroomId]);

  useEffect(() => {
    fetchAssignment();
  }, [fetchAssignment]);

  // Session length is a per-group-code config value, not part of the assignment
  // payload — the same hook the lesson and standard bundle views use, so all three
  // headers read and commit it identically.
  const allAssignments = useMemo(
    () => (draft?.bundles || []).flatMap((b) => b.assignments || []),
    [draft],
  );
  const { sessionLength, onSessionLengthChange } = useSessionLength(allAssignments, "ClassroomAssignmentView");

  const serverDraft = useMemo(() => (serverData ? toDraft(serverData) : null), [serverData]);
  const isDirty = useMemo(() => {
    if (!draft || !serverDraft) return false;
    return signature(draft.bundles, draft.unassigned)
      !== signature(serverDraft.bundles, serverDraft.unassigned);
  }, [draft, serverDraft]);

  const bundles = useMemo(() => draft?.bundles || [], [draft]);
  // Display order only — edits and the save diff key off assignment_id.
  const sortedBundles = useMemo(() => sortBundles(draft?.bundles || [], sortBy), [draft, sortBy]);
  const isFocused = isEditing && focusedId !== null;
  const visibleBundles = isFocused
    ? sortedBundles.filter((b) => b.assignment_id === focusedId)
    : sortedBundles;
  // Every entry the class shows, quick groups included — any of them can be the
  // last thing worked on. Unsorted, so the Sort By choice never moves the default.
  const entries = useMemo(() => [...bundles, ...quickGroups], [bundles, quickGroups]);
  const lastFocus = useLastFocus(classroomId, entries);
  const unassigned = draft?.unassigned || [];
  const assignedCount = bundles.reduce((acc, b) => acc + b.assignments.length, 0);
  const totalGroups = assignedCount + unassigned.length;
  const hasBundles = bundles.length > 0;
  const hasNoOtherGroups = !isLoading && Boolean(draft) && !hasBundles && !error && !hasQuickGroups;
  const isEmptyClass = hasNoOtherGroups && !hasWarmUps && !warmUpLoading;
  // An empty class keeps today's empty page; the one list needs a card.
  const showsOneList = layout === "one_list" && !isEmptyClass;
  const draftSettled = Boolean(draft) || Boolean(error);
  // Every assigned code across the class — candidates for "Clear More Group Codes".
  const clearable = bundles.flatMap((b) =>
    b.assignments.map((a) => ({ group_code: a.group_code, standard_code: a.standard_code, standard_name: a.standard_name })));

  // Adding an unassigned code (drag-drop or the Add Group Code menu) opens Edit
  // Group Focus so the educator chooses core/prereq/extension for the code.
  const goToEditGroupFocus = (assignmentId, code) => {
    const params = new URLSearchParams({
      classId: selectedClassId || "",
      classroomId,
      assignmentId,
      groupCode: code,
    });
    history.push(`${constants.EDIT_GROUP_FOCUS_PATH}?${params.toString()}`);
  };

  // Into the Question Set view for one group. The page resolves the bundle out of
  // the unified envelope and scopes by standard when it carries no lesson, so the
  // same params serve this flow too.
  const goToQuestionSet = (bundle, code, source) => {
    trackClickedButton("Edit Question Set", {
      page_name: "Dashboard",
      source,
      class: classroomId || null,
      group_code: code || null,
    });
    const params = new URLSearchParams({
      classId: selectedClassId || "",
      classroomId: classroomId || "",
      assignmentId: bundle?.assignment_id || "",
      groupCode: code,
    });
    history.push(`${constants.QUESTION_SET_PATH}?${params.toString()}`);
  };

  // ---- Draft mutations (local only; persisted on Save) ----
  const unassignCode = (code) => {
    setDraft((prev) => {
      const nextBundles = prev.bundles.map((b) => ({
        ...b,
        assignments: b.assignments.filter((a) => a.group_code !== code),
      }));
      const nextUnassigned = prev.unassigned.includes(code)
        ? prev.unassigned
        : [...prev.unassigned, code].sort(compareCode);
      return { ...prev, bundles: nextBundles, unassigned: nextUnassigned };
    });
  };

  // Duplicate = clone the card's row onto the first free code as a draft entry on the
  // same bundle, and enter edit mode so the teacher reviews before Save commits it.
  const duplicateCard = (bundleId, card) => {
    setDraft((prev) => {
      const free = prev.unassigned[0];
      if (!free) return prev;
      return {
        ...prev,
        bundles: prev.bundles.map((b) => (b.assignment_id === bundleId
          ? { ...b, assignments: [...b.assignments, { ...card, group_code: free }] }
          : b)),
        unassigned: prev.unassigned.filter((c) => c !== free),
      };
    });
  };

  // "Clear More Group Codes": release the selected assigned codes back to the pool
  // (draft only; persisted on Save), then close the modal.
  // Clear releases the selected codes immediately on confirm (not on Save): group
  // them by their owning bundle and PUT track_type:null per bundle, then reload so
  // the pool + cards reflect it. Uses serverDraft (committed state) to resolve
  // ownership, and drops any in-flight draft edits on reload.
  const handleClearConfirm = async (selection) => {
    setIsClearModalOpen(false);
    const byBundle = new Map();
    (serverDraft?.bundles || []).forEach((b) => {
      b.assignments.forEach((a) => {
        if (selection.has(a.group_code)) {
          if (!byBundle.has(b.assignment_id)) byBundle.set(b.assignment_id, []);
          byBundle.get(b.assignment_id).push(a.group_code);
        }
      });
    });
    if (byBundle.size === 0) return;
    try {
      await Promise.all([...byBundle.entries()].map(([aid, codes]) =>
        request(`groups/classrooms/${classroomId}/assignments/${aid}`, {
          method: "PUT",
          payload: { assignments: codes.map((code) => ({ group_code: code, track_type: null, standard_code: null })) },
        })));
      await fetchAssignment();
    } catch (e) {
      logEducatorDashboard("ClassroomAssignmentView: failed to clear group codes", {
        level: LogLevel.WARNING,
        event: "clear_error",
        statusCode: e?.status,
      });
      setError(e?.detail || e?.message || "Failed to clear group codes.");
    }
  };

  // Archive = delete the bundle (frees all its codes back to the pool). This is an
  // immediate, confirmed server action — not a draft edit — so the lesson is
  // removed rather than left behind as an empty card.
  const confirmArchive = async () => {
    if (!archiveTarget?.assignment_id || !classroomId) return;
    setArchiving(true);
    setError(null);
    try {
      await request(`groups/classrooms/${classroomId}/assignments/${archiveTarget.assignment_id}`, {
        method: "DELETE",
      });
      trackClickedButton("Archive Lesson", {
        page_name: "Dashboard",
        source: "classroom_assignment_view",
        class: classroomId || null,
        assignment_id: archiveTarget.assignment_id,
      });
      archivedListCard.current?.onArchived?.();
      setArchiveTarget(null);
      await fetchAssignment();
    } catch (e) {
      logEducatorDashboard("ClassroomAssignmentView: failed to archive bundle", {
        level: LogLevel.WARNING,
        event: "archive_error",
        statusCode: e?.status,
        endpoint: `groups/classrooms/${classroomId}/assignments/${archiveTarget.assignment_id}`,
      });
      setError(e?.detail || e?.message || "Failed to archive lesson.");
    } finally {
      setArchiving(false);
    }
  };


  // ---- Drag handling ----
  // Dropping an unassigned code into a lesson opens Edit Group Focus so the
  // educator chooses core/prereq/extension, rather than auto-assigning to core.
  const onDragStart = (event) => setActiveDragCode(event.active?.data?.current?.code || null);
  const onDragCancel = () => setActiveDragCode(null);
  const onDragEnd = (event) => {
    setActiveDragCode(null);
    const { active, over } = event;
    const code = active?.data?.current?.code;
    const overData = over?.data?.current;
    if (!code || !overData?.assignmentId) return;
    goToEditGroupFocus(overData.assignmentId, code);
  };

  // ---- Persist ----
  const handleSave = async () => {
    if (!isDirty || !serverDraft) { setIsEditing(false); leaveEdit(); return; }
    setSaving(true);
    setError(null);
    try {
      // 1) Per-bundle assignment diffs.
      for (const bundle of draft.bundles) {
        const serverBundle = serverDraft.bundles.find((b) => b.assignment_id === bundle.assignment_id);
        const serverByCode = new Map((serverBundle?.assignments || []).map((a) => [a.group_code, a]));
        const draftByCode = new Map(bundle.assignments.map((a) => [a.group_code, a]));
        const changes = [];
        // Added or changed.
        for (const a of bundle.assignments) {
          const prev = serverByCode.get(a.group_code);
          if (!prev || prev.standard_code !== a.standard_code || prev.track_type !== a.track_type) {
            changes.push({ group_code: a.group_code, track_type: a.track_type, standard_code: a.standard_code });
          }
        }
        // Removed → unassign.
        for (const a of (serverBundle?.assignments || [])) {
          if (!draftByCode.has(a.group_code)) {
            changes.push({ group_code: a.group_code, track_type: null, standard_code: null });
          }
        }
        if (changes.length > 0) {
          // eslint-disable-next-line no-await-in-loop
          await request(`groups/classrooms/${classroomId}/assignments/${bundle.assignment_id}`, {
            method: "PUT",
            payload: { assignments: changes },
          });
        }

        // The update body has no set pointer, so a duplicated card would land on
        // the full bank. Point the new code at the source group's set.
        const newSetTargets = bundle.assignments
          .filter((a) => a.question_set_id && !serverByCode.has(a.group_code))
          .reduce((acc, a) => {
            (acc[a.question_set_id] = acc[a.question_set_id] || []).push(a.group_code);
            return acc;
          }, {});
        // eslint-disable-next-line no-await-in-loop
        await Promise.all(Object.entries(newSetTargets)
          .map(([setId, codes]) => applyQuestionSet(setId, codes)));
      }

      trackClickedButton("Save Group Assignments", {
        page_name: "Dashboard",
        source: "classroom_assignment_view",
        class: classroomId || null,
      });
      await fetchAssignment();
      setIsEditing(false);
      leaveEdit();
    } catch (e) {
      logEducatorDashboard("ClassroomAssignmentView: failed to save assignment", {
        level: LogLevel.WARNING,
        event: "save_error",
        statusCode: e?.status,
        endpoint: `groups/classrooms/${classroomId}/assignments`,
      });
      setError(e?.detail || e?.message || "Failed to save changes.");
    } finally {
      setSaving(false);
    }
  };


  // ---- Navigation to existing structural-edit flows (guarded against unsaved drafts) ----
  const guardDirty = () => {
    if (!isDirty) return true;
    // eslint-disable-next-line no-alert
    return window.confirm("You have unsaved changes that will be lost. Continue?");
  };

  const onChangeLessonFocus = (bundle) => {
    if (!guardDirty()) return;
    const params = new URLSearchParams({
      classId: selectedClassId || "",
      classroomId,
      assignmentId: bundle.assignment_id,
      currentStandardCode: bundle.primary_standard_code || "",
    });
    history.push(`${constants.VIEW_CHANGE_STANDARD_PATH}?${params.toString()}`);
  };

  const onEditCardStandard = (bundle, groupCode) => {
    if (!guardDirty()) return;
    trackClickedButton("Edit Group Focus", {
      page_name: "Dashboard",
      source: "classroom_assignment_view",
      class: classroomId || null,
      assignment_id: bundle?.assignment_id || null,
      group_code: groupCode || null,
    });
    const params = new URLSearchParams({
      classId: selectedClassId || "",
      classroomId,
      assignmentId: bundle.assignment_id,
      groupCode: groupCode || "",
    });
    history.push(`${constants.EDIT_GROUP_FOCUS_PATH}?${params.toString()}`);
  };

  const onAssignNewGroup = () => {
    if (!guardDirty()) return;
    const params = new URLSearchParams({
      classId: selectedClassId || "",
      intent: "assign",
      classroomId,
      totalAvailable: String(unassigned.length),
    });
    // Seed the picker from what the class last worked on — same rule as the
    // lesson flow, and independent of the Sort By choice.
    if (lastFocus?.standardCode) params.set("standard", lastFocus.standardCode);
    history.push(`${constants.GROUP_CODE_STANDARD_SEARCH_PATH.replace(":groupCode", "_assign")}?${params.toString()}`);
  };

  const onCreateQuickGroup = () => {
    if (!guardDirty()) return;
    const params = new URLSearchParams({
      classroomId,
      classId: selectedClassId || classroomId || "",
      // Open the grade dropdown on the class's working grade, not the teacher's.
      ...(lastFocus?.grade ? { grade: lastFocus.grade } : {}),
    });
    history.push(`${constants.CREATE_QUICK_GROUP_PATH}?${params.toString()}`);
  };

  const onOpenCreateMenu = () => {
    trackClickedButton("Create Groups", {
      page_name: "Dashboard",
      source: "classroom_assignment_view",
      class: classroomId || null,
    });
  };

  // Each warm-up entry reports its click before the unsaved-changes prompt,
  // whatever the teacher then chooses.
  const openWarmUpPicker = () => {
    if (!guardDirty()) return;
    history.push(warmUpPickerUrl(classroomId));
  };

  const onCreateWarmUpGroups = () => {
    trackClickedButton("Warm-Up Groups", {
      page_name: "Dashboard",
      source: "classroom_assignment_view",
      class: classroomId || null,
    });
    openWarmUpPicker();
  };

  const onCreateWarmUpGroupsCard = () => {
    trackClickedButton("Create Warm-Up Groups", {
      page_name: "Dashboard",
      class: classroomId || null,
      source: "classroom_assignment_view_empty_state",
    });
    openWarmUpPicker();
  };

  const onSeeAllGroupTypes = () => {
    trackClickedButton("See All Group Types", {
      page_name: "Dashboard",
      class: classroomId || null,
      source: "classroom_assignment_view_empty_state",
    });
  };

  const onPickWarmUp = () => {
    trackClickedButton("Pick a Warm-Up Challenge", {
      page_name: "Dashboard",
      class: classroomId || null,
      source: "classroom_assignment_view_empty_state",
    });
    openWarmUpPicker();
  };

  // The list's card for the bundle being archived, told once the archive went through.
  const archivedListCard = useRef(null);

  const renderBundle = (bundle, listCard) => (
    <AssignmentAccordion
      {...oneListCardProps(listCard)}
      key={bundle.assignment_id}
      bundle={bundle}
      draftAssignments={bundle.assignments}
      unassignedCodes={unassigned}
      sessionLength={sessionLength ?? DEFAULT_SESSION_LENGTH}
      sessionLengthEditable
      keepMetaWhileEditing
      isEditing={isEditing}
      onEnterEdit={() => enterEdit(bundle)}
      onSave={handleSave}
      saving={saving}
      onSessionLengthChange={onSessionLengthChange}
      onArchive={() => {
        archivedListCard.current = listCard;
        setArchiveTarget(bundle);
      }}
      onCopyLesson={onLessonCopied ? () => setCopyTarget(bundle) : undefined}
      onChangeLessonFocus={() => onChangeLessonFocus(bundle)}
      onAddCode={(code) => goToEditGroupFocus(bundle.assignment_id, code)}
      onTrashCode={unassignCode}
      onDuplicateCard={(card) => {
        enterEdit(bundle);
        duplicateCard(bundle.assignment_id, card);
      }}
      onEditCardStandard={(groupCode) => onEditCardStandard(bundle, groupCode)}
      onViewQuestions={(card) => setViewQuestionsTarget({
        bundle,
        code: card.standard_code,
        name: card.standard_name,
        questionFilter: card.question_filter,
        trackType: card.track_type,
        groupCode: card.group_code,
        questionSetId: card.question_set_id || null,
        focus: resolveGroupCardTitle(card, bundle),
      })}
    />
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <div style={{ display: "flex", alignItems: "flex-start" }}>
        {/* Main column */}
        <div style={{ flex: 1, minWidth: 0, paddingBottom: 96 }}>
          {!isFocused && !showsOneList && (
          <>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 8, marginBottom: 16 }}>
            <div style={{ maxWidth: 200, width: "100%" }}>
              <SelectDropdown
                label="Class"
                value={selectedClassId}
                displayText={classDisplayName(classes?.find((c) => c.id === selectedClassId)) || "Select a class"}
                options={(classes || []).map((cls) => ({ value: cls.id, label: cls.name, school_year: cls.school_year }))}
                groupBySchoolYear
                onChange={onClassChange}
              />
            </div>
            {onEditClass && <EditClassButton onClick={onEditClass} />}
          </div>
          {layout === "pending" && <GroupsSkeleton withHeader={false} />}
          {layout === "one_list" && warmUpError && <WarmUpGroupsLoadError onRetry={refetchWarmUps} style={{ marginBottom: 24 }} />}
          {layout === "sections" && (
          <>
          {(hasBundles || hasQuickGroups || hasWarmUps) && (
            <CreateGroupsBar
              available={unassigned.length}
              total={totalGroups}
              onCreateLessonGroups={onAssignNewGroup}
              onCreateQuickGroup={onCreateQuickGroup}
              lessonGroupsDetail={LESSON_GROUPS_DETAIL}
              createControls={createControlsFor(warmUpChallenges)}
              onOpenCreateMenu={onOpenCreateMenu}
              onCreateWarmUpGroups={onCreateWarmUpGroups}
              menuResetKey={classroomId}
              focusMenuButton={focusAfterLastWarmUp && !hasWarmUps}
              onMenuButtonFocused={clearFocusAfterLastWarmUp}
            />
          )}

          {/* Quick groups list, above the assigned bundles (same order as the
              lesson list) — under the shared Class + Create Groups bar. */}
          <QuickGroupsSection
            quickGroups={quickGroups}
            classroomId={classroomId}
            onChanged={onQuickGroupsChanged}
            sortOptions={STANDARD_SORT_OPTIONS}
            style={{ marginBottom: 24 }}
          />
          </>
          )}
          </>
          )}

          {isFocused && (
            <FocusedEditHeader
              className={classDisplayName(classes?.find((c) => c.id === selectedClassId))}
              availableCount={unassigned.length}
              totalGroups={totalGroups}
              onClear={clearable.length > 0 ? () => setIsClearModalOpen(true) : undefined}
            />
          )}

          {showsOneList && (
            <OneListPage
              focusedKey={isFocused ? `lesson:${focusedId}` : null}
              classes={classes}
              selectedClassId={selectedClassId}
              onClassChange={onClassChange}
              onEditClass={onEditClass}
              lessons={bundles}
              quickGroups={quickGroups}
              warmUps={warmUpAssignments}
              classroomId={classroomId}
              renderLesson={renderBundle}
              settled={sourcesSettled && draftSettled}
              available={unassigned.length}
              total={totalCodes}
              createMenu={{
                onOpen: onOpenCreateMenu,
                onCreateLessonGroups: onAssignNewGroup,
                onCreateQuickGroup,
                onCreateWarmUpGroups,
                focusRequest: focusAfterLastWarmUp,
                onFocusRequestDone: clearFocusAfterLastWarmUp,
              }}
              warmUpError={warmUpError}
              listSettings={listSettings}
              onListSettingsChange={onListSettingsChange}
              sourcesReadAt={sourcesReadAt}
              source="classroom_assignment_view"
              warmUpActions={{
                source: "classroom_assignment_view",
                classes,
                onCopied: onLessonCopied,
                onAccountRefresh,
                refetchWarmUps,
                replaceAssignment: replaceWarmUpAssignment,
                onArchived: fetchAssignment,
                guardArchive: guardDirty,
              }}
              onNoCardLeft={() => setFocusAfterLastWarmUp(true)}
              onQuickGroupsChanged={() => Promise.all([onQuickGroupsChanged?.(), fetchAssignment?.()])}
              guardNavigation={guardDirty}
              lessonGroupsDetail={LESSON_GROUPS_DETAIL}
            />
          )}
          <div ref={stackRef} style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            {!isFocused && layout === "sections" && hasBundles && (
              <ListingHeader
                title="Lessons"
                sortValue={sortBy}
                sortOptions={STANDARD_SORT_OPTIONS}
                onSortChange={setSortBy}
              />
            )}
            {layout === "sections" && isLoading && !draft && (
              <GroupsSkeleton withHeader={false} />
            )}
            {error && <div role="alert" style={{ color: "crimson", padding: 16 }}>Error: {error}</div>}
            {isEmptyClass && !warmUpChallenges.isLoading && (warmUpChallenges.enabled ? (
              <WarmUpEmptyStates
                key={classroomId}
                lessonGroupsDetail={LESSON_GROUPS_DETAIL}
                onPickWarmUp={onPickWarmUp}
                onCreateLessonGroups={onAssignNewGroup}
                lessonGroupsDisabled={unassigned.length === 0}
                lessonGroupsTitle={unassigned.length === 0 ? "All group codes are in use." : undefined}
                onCreateQuickGroup={onCreateQuickGroup}
                onCreateWarmUpGroups={onCreateWarmUpGroupsCard}
                onSeeAllGroupTypes={onSeeAllGroupTypes}
                focusTitle={focusAfterLastWarmUp}
                onTitleFocused={clearFocusAfterLastWarmUp}
              />
            ) : (
              <GroupsEmptyState
                primaryLabel="Create Lesson Groups"
                onPrimary={onAssignNewGroup}
                primaryDisabled={unassigned.length === 0}
                primaryTitle={unassigned.length === 0 ? "All group codes are in use." : undefined}
                onQuickGroup={onCreateQuickGroup}
                lessonGroupsDetail={LESSON_GROUPS_DETAIL}
              />
            ))}
            {hasBundles && layout === "sections" && visibleBundles.map((bundle) => renderBundle(bundle))}
          </div>
        </div>

      </div>

      <DragOverlay>{activeDragCode ? <UnassignedChip code={activeDragCode} dragging /> : null}</DragOverlay>

      <ClearGroupCodesModal
        isOpen={isClearModalOpen}
        onClose={() => setIsClearModalOpen(false)}
        clearable={clearable}
        onConfirm={handleClearConfirm}
      />

      <ArchiveLessonModal
        isOpen={Boolean(archiveTarget)}
        onClose={() => setArchiveTarget(null)}
        onConfirm={confirmArchive}
        archiving={archiving}
      />

      <ViewQuestionsModal
        isOpen={Boolean(viewQuestionsTarget)}
        onClose={() => { setViewQuestionsTarget(null); setQuestionFilterSaveError(null); }}
        standardCode={viewQuestionsTarget?.code}
        standardName={viewQuestionsTarget?.name}
        questionFilter={viewQuestionsTarget?.questionFilter}
        questionSetId={viewQuestionsTarget?.questionSetId}
        groupCode={viewQuestionsTarget?.groupCode}
        focus={viewQuestionsTarget?.focus}
        editable
        saving={savingQuestionFilter}
        saveError={questionFilterSaveError}
        onCreateQuestionSet={() => {
          const target = viewQuestionsTarget;
          if (!target?.groupCode) return;
          setViewQuestionsTarget(null);
          goToQuestionSet(target.bundle, target.groupCode, "view_questions_modal");
        }}
        onSave={async (nextQuestionFilter, filteredTotal) => {
          const target = viewQuestionsTarget;
          if (!target) return;
          setSavingQuestionFilter(true);
          setQuestionFilterSaveError(null);
          try {
            // Track spelling is per bundle kind: standard bundles keep the plural
            // "extensions" their rows are stored with (only lesson bundles use the
            // singular form), so the card's value goes through unmapped.
            await patchGroupAssignments(classroomId, target.bundle?.assignment_id, [{
              group_code: target.groupCode,
              track_type: target.trackType,
              standard_code: target.code,
              question_filter: nextQuestionFilter,
            }]);
            await fetchAssignment();
            setViewQuestionsTarget(null);
            // Other groups on this topic get the same offer the My Question Set
            // flow makes after a save (OKO2-3803).
            const candidates = reuseFilterCandidates(target.bundle?.assignments, {
              groupCode: target.groupCode,
              trackType: target.trackType,
              standardCode: target.code,
            });
            if (candidates.length > 0) {
              setReuseOffer({
                bundle: target.bundle,
                groupCode: target.groupCode,
                trackType: target.trackType,
                standardCode: target.code,
                standardName: target.name,
                filter: nextQuestionFilter,
                total: filteredTotal,
                candidates,
              });
            }
          } catch (e) {
            logEducatorDashboard("ClassroomAssignmentView: failed to save question filter", {
              level: LogLevel.WARNING,
              event: "question_filter_save_error",
              statusCode: e?.status,
              groupCode: target.groupCode,
            });
            setQuestionFilterSaveError(e?.detail || e?.message || "Failed to save filters.");
          } finally {
            setSavingQuestionFilter(false);
          }
        }}
      />

      <ReuseFilterModal
        offer={reuseOffer}
        bundle={reuseOffer?.bundle || {}}
        classroomId={classroomId}
        assignmentId={reuseOffer?.bundle?.assignment_id}
        onClose={() => setReuseOffer(null)}
        onApplied={fetchAssignment}
      />

      <CopyLessonModal
        isOpen={Boolean(copyTarget)}
        onClose={() => setCopyTarget(null)}
        classroomId={classroomId}
        assignmentId={copyTarget?.assignment_id}
        lessonLabel={copyTarget?.primary_standard_code}
        lessonName={copyTarget?.primary_standard_name || copyTarget?.primary_standard_code}
        classes={classes}
        // One free code needed per real-track group in this bundle.
        requiredCount={copyTarget?.assignments?.length || 0}
        onCopied={(copies) => {
          const name = copyTarget?.primary_standard_name || copyTarget?.primary_standard_code;
          setCopyTarget(null);
          onLessonCopied?.(copies, name);
        }}
        onAccountRefresh={onAccountRefresh}
      />
    </DndContext>
  );
};
